/**
 * Aleatorización del CAPI.
 *
 * PRINCIPIO DE DISEÑO: TODA la aleatorización es DETERMINISTA a partir del
 * identificador de la entrevista. Esto no es un detalle de implementación,
 * resuelve cuatro requisitos simultáneos:
 *
 *  1. §Bloque 3 exige que el CAPI conserve EL MISMO orden de los personajes
 *     en todas las preguntas del bloque para un entrevistado, y §Bloque 9 que
 *     la tarjeta de vinculación use ese mismo orden. Con una semilla derivada
 *     del identificador de la entrevista, el orden se recalcula idéntico en
 *     cualquier momento y en cualquier dispositivo.
 *  2. La PWA trabaja con cola de reintento: si una entrevista se reenvía, el
 *     servidor debe reconstruir exactamente la misma rotación para validar la
 *     captura. Sin determinismo, un reintento parecería otra entrevista.
 *  3. §11 pide conservar la bitácora de campo para auditoría: un tercero puede
 *     reproducir la rotación que vio cada entrevistado.
 *  4. Permite que el orden se calcule offline en el dispositivo sin pedirlo al
 *     servidor.
 *
 * Las asignaciones que requieren BALANCEO GLOBAL (celdas del experimento X1 y
 * personajes de I6 por menor cobertura) no pueden ser puramente locales: el
 * servidor las decide con el estado acumulado de la muestra y las devuelve a
 * la PWA, que las guarda con la entrevista.
 */
import { KNOWS_PERSONAJE_WELL_CODE } from './scales.js';
/* -------------------------------------------------------------------------- */
/*                        Generador pseudoaleatorio                           */
/* -------------------------------------------------------------------------- */
/** cyrb53: hash de 53 bits, rápido y con buena dispersión. */
function hashString(input, seed = 0) {
    let h1 = 0xdeadbeef ^ seed;
    let h2 = 0x41c6ce57 ^ seed;
    for (let i = 0; i < input.length; i++) {
        const ch = input.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}
/** mulberry32, determinista y suficiente para rotaciones de cuestionario. */
export function createRng(seedInput) {
    let state = hashString(seedInput) >>> 0;
    const next = () => {
        state = (state + 0x6d2b79f5) >>> 0;
        let t = state;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    return { next, nextInt: (max) => Math.floor(next() * max) };
}
/** Fisher-Yates con el RNG dado. No muta la entrada. */
export function shuffle(items, rng) {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i--) {
        const j = rng.nextInt(i + 1);
        const a = out[i];
        const b = out[j];
        out[i] = b;
        out[j] = a;
    }
    return out;
}
/** Semilla de un propósito concreto dentro de una entrevista. */
export function seedFor(interviewId, purpose) {
    return `${interviewId}::${purpose}`;
}
/**
 * Rota las filas respetando dos reglas:
 *
 *  - Una fila con `rotates: false` CONSERVA SU POSICIÓN. Lo necesita P4, donde
 *    la coalición Juntos Haremos Historia se lee siempre al final, después de
 *    los 9 partidos rotados.
 *  - Las filas se mezclan ÚNICAMENTE dentro de su `rotationGroup`, y los
 *    grupos conservan el orden en que aparecen. Lo exigía §Bloque 7: primero
 *    los 9 partidos solos (rotados entre sí), después las 3 alianzas (rotadas
 *    entre sí), con el orden "solos → alianzas" FIJO.
 */
export function rotateRows(rows, rng) {
    // Índices de las filas fijas, que no se mueven.
    const fixedAt = new Map();
    const movable = [];
    rows.forEach((row, index) => {
        if (row.rotates === false)
            fixedAt.set(index, row);
        else
            movable.push(row);
    });
    // Mezcla dentro de cada grupo, preservando el orden de aparición de grupos.
    const groupOrder = [];
    const byGroup = new Map();
    for (const row of movable) {
        const group = row.rotationGroup ?? '__default__';
        if (!byGroup.has(group)) {
            byGroup.set(group, []);
            groupOrder.push(group);
        }
        byGroup.get(group).push(row);
    }
    const shuffled = groupOrder.flatMap((group) => shuffle(byGroup.get(group), rng));
    // Reinserta las fijas en sus posiciones originales.
    const out = [];
    let cursor = 0;
    for (let i = 0; i < rows.length; i++) {
        const fixed = fixedAt.get(i);
        if (fixed)
            out.push(fixed);
        else
            out.push(shuffled[cursor++]);
    }
    return out;
}
/**
 * Orden de los personajes del catálogo para una entrevista.
 *
 * §Bloque 3: "Orden rotado aleatoriamente por entrevista (el CAPI conserva el
 * mismo orden en todas las preguntas del bloque para ese entrevistado)".
 * §Bloque 9 reutiliza este mismo orden, por eso el propósito de la semilla es
 * compartido y no específico de una pregunta.
 */
export function personajeOrderFor(interviewId, personajeIds) {
    return shuffle(personajeIds, createRng(seedFor(interviewId, 'personaje-order')));
}
/** Orden de los 9 partidos para una entrevista (bloques 5 y 7). */
export function partyOrderFor(interviewId, partyIds) {
    return shuffle(partyIds, createRng(seedFor(interviewId, 'party-order')));
}
/** Orden rotado de una batería cualquiera, estable por entrevista y pregunta. */
export function rowOrderFor(interviewId, questionCode, rows) {
    return rotateRows(rows, createRng(seedFor(interviewId, `rows:${questionCode}`)));
}
/**
 * §5.3.3: "entrevistar a quien cumpla años más próximamente (alternar con
 * 'último cumpleaños' entre encuestadores)".
 *
 * La alternancia se deriva del identificador del encuestador para que cada uno
 * aplique siempre la misma regla —si cambiara entre viviendas, el encuestador
 * podría elegir la que le convenga— y para que el reparto entre reglas quede
 * balanceado en el equipo.
 */
export function birthdayRuleFor(encuestadorId) {
    return hashString(encuestadorId, 0x5eed) % 2 === 0 ? 'proximo' : 'ultimo';
}
/**
 * §Bloque 3, I6: "el CAPI asigna 2 personajes conocidos al azar por
 * entrevistado, PRIORIZANDO A LOS DE MENOR COBERTURA".
 *
 * Estrategia: entre los personajes que el entrevistado declaró conocer, se
 * ordenan por cobertura acumulada ascendente y se toman los `size` primeros;
 * los empates se rompen al azar con la semilla de la entrevista, de modo que
 * la prioridad por cobertura no introduzca un orden sistemático.
 */
export function assignI6Personajes(interviewId, knownPersonajeIds, coverage, size = 2, 
/**
 * Respuesta de I1 por personaje, para preferir a quien se conoce BIEN.
 *
 * Opcional porque los dispositivos con una versión anterior de la PWA sólo
 * mandan la lista de conocidos. Sin este dato el comportamiento es el de
 * antes, que es lo correcto: degradar, no fallar.
 */
awareness) {
    if (knownPersonajeIds.length === 0)
        return [];
    const counts = new Map(coverage.map((c) => [c.personajeId, c.count]));
    const rng = createRng(seedFor(interviewId, 'i6-assignment'));
    // Se mezcla primero para que el desempate sea aleatorio y no alfabético.
    const candidates = shuffle(knownPersonajeIds, rng);
    /**
     * «Lo he oído mencionar» entra sólo si no alcanzan los que se conocen bien.
     *
     * MEDIDO EN EL PILOTO, no supuesto: de las 24 filas de I6 levantadas, 13
     * cayeron sobre personajes marcados `ha_oido`, y sus respuestas fueron
     * «No la conozco», «Nada», «La eh visto pero no sé». I6 pide una
     * asociación espontánea y quien sólo oyó el nombre no tiene ninguna, así
     * que esas filas entran como ruido en el indicador de §10.1.4 y, en la
     * puerta, hacen que la pregunta suene absurda.
     *
     * No se excluyen del todo porque dejarían sin I6 a quien no conozca bien a
     * dos: en el piloto 11 de 12 entrevistados tenían al menos dos
     * `conoce_bien` —3.6 de media—, pero uno no tenía ninguno. Preferir es
     * mejor que excluir: conserva la cobertura y mejora la base.
     */
    const rank = (id) => {
        if (!awareness)
            return 0;
        return awareness[id] === KNOWS_PERSONAJE_WELL_CODE ? 0 : 1;
    };
    candidates.sort((a, b) => {
        const porConocimiento = rank(a) - rank(b);
        if (porConocimiento !== 0)
            return porConocimiento;
        return (counts.get(a) ?? 0) - (counts.get(b) ?? 0);
    });
    return candidates.slice(0, Math.min(size, candidates.length));
}
/**
 * §Bloque 8: el CAPI asigna a cada entrevistado UNA SOLA versión.
 *
 * Se asigna la celda con mayor DÉFICIT relativo a su peso, es decir la que va
 * más atrasada respecto a la distribución objetivo. Así las celdas terminan
 * balanceadas sin depender de que la aleatoriedad simple se equilibre sola,
 * que con ~200 casos por celda puede desviarse de forma apreciable.
 *
 * Nota de denominador: la asignación se hace ANTES de saber si el entrevistado
 * conoce al personaje de la celda. Si no lo conoce, X1 no se aplica pero la
 * asignación queda registrada, de modo que el analista pueda distinguir "no
 * elegible para la celda" de "no respondió".
 */
export function assignExperimentCell(interviewId, cells) {
    if (cells.length === 0)
        return null;
    const totalWeight = cells.reduce((a, c) => a + c.weight, 0);
    if (totalWeight <= 0)
        return null;
    const totalAssigned = cells.reduce((a, c) => a + c.count, 0);
    let best = [];
    let bestDeficit = -Infinity;
    for (const cell of cells) {
        const expected = ((totalAssigned + 1) * cell.weight) / totalWeight;
        const deficit = expected - cell.count;
        if (deficit > bestDeficit + 1e-9) {
            bestDeficit = deficit;
            best = [cell];
        }
        else if (Math.abs(deficit - bestDeficit) <= 1e-9) {
            best.push(cell);
        }
    }
    // Empate: se rompe con la semilla de la entrevista.
    const rng = createRng(seedFor(interviewId, 'x1-cell'));
    return best[rng.nextInt(best.length)].cellId;
}
/* -------------------------------------------------------------------------- */
/*                      Recortes del piloto (§8.1)                            */
/* -------------------------------------------------------------------------- */
/**
 * Decide si una entrevista conserva una pregunta sometida a submuestreo de
 * entrevistados (§8.1.2 M2, §8.1.4 A2/A3/A9).
 *
 * Determinista por entrevista y por recorte: el reparto es estable y
 * reproducible, y dos recortes distintos no caen sobre las mismas entrevistas.
 */
export function respondentInSubsample(interviewId, trimKey, share) {
    if (share >= 1)
        return true;
    if (share <= 0)
        return false;
    return createRng(seedFor(interviewId, `trim:${trimKey}`)).next() < share;
}
/**
 * Selecciona las filas que conserva una pregunta sometida a submuestreo de
 * filas (§8.1.1: 3 de los 5 temas del Bloque 9).
 *
 * Conserva el orden rotado que ya tenía la batería para no reintroducir un
 * sesgo de orden.
 */
export function selectSubsampleRows(interviewId, trimKey, rotatedRows, rowsKept) {
    if (rowsKept >= rotatedRows.length)
        return [...rotatedRows];
    const rng = createRng(seedFor(interviewId, `trim-rows:${trimKey}`));
    const chosen = new Set(shuffle(rotatedRows, rng).slice(0, rowsKept).map((r) => r.code));
    return rotatedRows.filter((r) => chosen.has(r.code));
}
//# sourceMappingURL=randomization.js.map